import { getAuth, GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { Notification } from "../notification_handler";
import { vespidFunctionsAPI } from "../api";
import { NavigateFunction } from "react-router-dom";
import axios from "axios";

// Error thrown when user is already associated with a different partner
export class PartnerConflictError extends Error {
  currentPartner: { id: string; name: string };
  newPartnerId: string;

  constructor(currentPartner: { id: string; name: string }, newPartnerId: string) {
    super("User is already associated with a different partner");
    this.name = "PartnerConflictError";
    this.currentPartner = currentPartner;
    this.newPartnerId = newPartnerId;
  }
}

class AuthenticationHelper {
  /**
   * Function that checks for Firebase Error response
   */
  public authErrorHandler(errorCode: any): void {
    switch (errorCode) {
      case "auth/email-already-in-use":
        Notification.error(
          "You have signed up on Vespid. Please login using the email you provided."
        );
        break;
      case "auth/user-not-found":
        Notification.error(
          "Incorrect email or password. Please try again or recover your account."
        );
        break;
      case "auth/wrong-password":
        Notification.error(
          "Incorrect email or password. Please try again or recover your account."
        );
        break;
      case "auth/invalid-login-credentials":
        Notification.error(
          "Incorrect email or password. Please try again or recover your account."
        );
        break;
      case "auth/invalid-credential":
        Notification.error(
          "Incorrect email or password. Please try again or recover your account."
        );
        break;
      case "auth/too-many-requests":
        Notification.error(
          "Access to this account has been temporarily disabled due to many failed login attempts."
        );
    }
  }

  /**
   * @param errorCode Apple login error code
   * @description Function that handles Apple login errors
   * https://docs.expo.dev/versions/latest/sdk/apple-authentication/#error-codes
   */
  public appleAuthErrorHandler(errorCode: any) {
    switch (errorCode) {
      case "ERR_INVALID_OPERATION":
        Notification.error("Invalid login operation. Please try again later.");
        break;
      case "ERR_INVALID_RESPONSE":
        Notification.error("Invalid login response. Please try again later.");
        break;
      case "ERR_INVALID_SCOPE":
        Notification.error("Invalid login scope. Please try again later.");
        break;
      case "ERR_REQUEST_CANCELED":
        Notification.error(
          "Login request was canceled. Please try again later."
        );
        break;
      case "ERR_REQUEST_FAILED":
        Notification.error("Login request failed. Please try again later.");
        break;
      case "ERR_REQUEST_NOT_HANDLED":
        Notification.error(
          "Login request was not correctly handled. Please try again later."
        );
        break;
      case "ERR_REQUEST_NOT_INTERACTIVE":
        Notification.error(
          "Login request was not interactive. Please try again later."
        );
        break;
      case "ERR_RESPONSE_NOT_RECEIVED":
        Notification.error(
          "Login response was not received. Please try again later."
        );
        break;
    }
  }

  public async appleSignIn (navigate: NavigateFunction, partnerId?: string): Promise<void> {
    try {
      const appleAuth = getAuth();
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");

      await signInWithPopup(appleAuth, provider);
      
      // Complete OAuth user setup (creates missing data + partner tagging)
      await this.completeOAuthWithPartnerHandling(partnerId);
      
      Notification.success("Your login is successful! You can now use the Vespid app.");
      navigate("/install");
    } catch (error: any) {
      // Re-throw PartnerConflictError for the calling component to handle
      if (error instanceof PartnerConflictError) {
        throw error;
      }
      if (error.code) {
        this.appleAuthErrorHandler(error.code);
      }
    }
  }

  public async googleSignIn(navigate: NavigateFunction, partnerId?: string): Promise<void> {
    try {
      const googleAuth = getAuth();
      const provider = new GoogleAuthProvider();

      await signInWithPopup(googleAuth, provider);
      
      // Complete OAuth user setup (creates missing data + partner tagging)
      await this.completeOAuthWithPartnerHandling(partnerId);
      
      Notification.success("Your login is successful! You can now use the Vespid app.");
      navigate("/install");
    } catch (error: any) {
      // Re-throw PartnerConflictError for the calling component to handle
      if (error instanceof PartnerConflictError) {
        throw error;
      }
      if (error.code) {
        this.authErrorHandler(error.code);
      }
    }
  }

  /**
   * Complete OAuth flow with partner conflict handling
   * @throws {PartnerConflictError} if user is associated with a different partner
   */
  private async completeOAuthWithPartnerHandling(partnerId?: string): Promise<void> {
    try {
      await vespidFunctionsAPI.post("/account/oauth/complete", {
        partner_id: partnerId || undefined,
      });
    } catch (error: any) {
      // Handle 409 partner conflict
      if (axios.isAxiosError(error) && 
          error.response?.status === 400 && 
          error.response?.data?.error?.code === 'failed-precondition') {
        const currentPartner = error.response.data?.current_partner;
        if (currentPartner && partnerId) {
          throw new PartnerConflictError(currentPartner, partnerId);
        }
      }
      throw error;
    }
  }

  /**
   * Complete OAuth with partner replacement confirmation
   */
  public async completeOAuthWithReplacement(partnerId: string): Promise<void> {
    await vespidFunctionsAPI.post("/account/oauth/complete", {
      partner_id: partnerId,
      confirm_replace: true,
    });
  }
}

export const authenticationAPI = new AuthenticationHelper();
